import { Stack, Button, Menu, MenuItem, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Box } from '@mui/material';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCategoryData, getFacilityVendors, getRentalItems, closeOrderItems, updateOrderStatus, pickupOrderItemsRequest } from '../../../redux/slices/rentals';
import { useDispatch, useSelector } from '../../../redux/store';
import Iconify from '../../../reusable-components/iconify';
import DownloadRentalOrderDropdown from './DownloadRentalOrderDropdown'
import { ScopeGuard, useScopeCheck } from '../../../reusable-components/scopes';
import { useSnackbar } from '../../../reusable-components/snackbar';
import { AwaitButton } from '../../../reusable-components/await-button';
import { punchoutOrder} from '../../../api/rentals';


CustomToolbar.propTypes = {
  setSelectedRowsToClose: PropTypes.func,
  setOpenOrderApproveModal: PropTypes.func,
  setIsOrderRejected: PropTypes.func,
  setSelectedRowsToPickup: PropTypes.func,
  setIsConfirmMode: PropTypes.func,
  setIsRequestMode: PropTypes.func,
  selectedRowsToClose: PropTypes.array,
  isConfirmMode: PropTypes.bool,
  selectedRowsToPickup: PropTypes.array,
  isRequestMode: PropTypes.bool,
  orderId: PropTypes.number,
  rows: PropTypes.array,
  setSplitOrder: PropTypes.func,
  facility: PropTypes.number,
  residentId: PropTypes.number,
  isApproveAllowed: PropTypes.bool,
  allRequested: PropTypes.bool,
  isPunchoutOrder: PropTypes.bool
};

export default function CustomToolbar({allRequested, setSelectedRowsToClose, setOpenOrderApproveModal, setIsOrderRejected, setSelectedRowsToPickup, setIsConfirmMode, setIsRequestMode, selectedRowsToClose, isConfirmMode, selectedRowsToPickup, isRequestMode, orderId, rows, setSplitOrder, facility, residentId, isApproveAllowed, isPunchoutOrder }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const openMenu = Boolean(menuAnchorEl);
  const navigate = useNavigate();
  const [categoryOptions, setCategoryOptions] = useState([]);
  const dispatch = useDispatch();
  const [confirmPunchout, setConfirmPunchout] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const {
      data: { categoryData, items, facilityVendors },
  } = useSelector((state) => state.rentals);
  const location = useLocation();
  const orderStatus = location.pathname.split('/').pop().replace('-rentals', '').toLowerCase();
  useEffect(() => {
     if (facilityVendors.length === 0) dispatch(getFacilityVendors(facility));
     if (categoryData.length === 0)dispatch(getCategoryData());
     if (items.length === 0)dispatch(getRentalItems());
  }, [dispatch]);
  
  const userEmail = useSelector(
    (state) => state.user.user.email
  );
  const userSiteId = useSelector(
    (state) => state.user.user.siteId
  );
  useEffect(() => {
    const listCategories = items.filter((item) => facilityVendors.some((fv) => fv.vendorId === item.vendorId))
      .map((item) => ({  categoryName: item?.categoryName,
        categoryId: item?.categoryId,
        categoryIcon: categoryData.find(x => x.categoryId === item.categoryId)?.categoryIcon
  }))

  const options = listCategories.reduce((acc, item) => {
    if(!acc.some(c => c.label === item.categoryName)){
      acc.push({
        label: item.categoryName,
        icon: item.categoryIcon,
        id : item.categoryId
      })
    }
      return acc;
    }, []);
    setCategoryOptions(options)
  }, [items, facilityVendors, categoryData])

  const hasSubmitAccess = useScopeCheck(isPunchoutOrder ? ['Rental-Order-Punchout'] : ['Rental-Order-Submit'], true);

  const enableConfirmMode = () => {
    setIsConfirmMode(true);
    setSelectedRowsToClose([]);
  };

  const enableRequestPickupMode = () => {
    setIsRequestMode(true);
    setSelectedRowsToPickup([]);
  };

  const confirmCloseOut = async () => {
    if (selectedRowsToClose.length === 0) {
      enqueueSnackbar('No rows selected to close out', { variant: 'warning' });
      return;
    }
    try {
      const response = await dispatch(closeOrderItems(selectedRowsToClose, orderId, isPunchoutOrder))
      if(response?.status === 200){
          enqueueSnackbar('Items successfully closed', { variant: 'success' });
      } else {
          enqueueSnackbar('Error closing out items', { variant: 'error' });
      }
      setIsConfirmMode(false);
      navigate(`/dashboard/rentals/current-rentals`);
    } catch (error) {
        enqueueSnackbar('Error closing out rows', { variant: 'error' });
    }
  };

  const requestPickup = async () => {
    if (selectedRowsToPickup.length === 0) {
      enqueueSnackbar('No rows selected to request pickup', { variant: 'warning' });
      return;
    }
    try {
      const response = await dispatch(pickupOrderItemsRequest(selectedRowsToPickup, orderId, userEmail, isPunchoutOrder))
      if(response?.success === true){
          enqueueSnackbar(response.message || 'Successfully requested pickup', { variant: 'success' });
      } else {
          enqueueSnackbar(response?.message || 'Error requesting pickup', { variant: 'error' });
      }
      setIsRequestMode(false);
    } catch (error) {
        enqueueSnackbar(error?.response?.data?.message || 'Error requesting pickup', { variant: 'error' });
    }
  };
          
  const rejectOrder = () => {
    setIsOrderRejected(true);
    dispatch(updateOrderStatus(rows, true, orderId, isPunchoutOrder)).then((response) => {
      if(response?.status === 200 && response?.data?.success === true){ 
        enqueueSnackbar('Rental Order Rejected', { variant: 'error' });
      }
      else{
        enqueueSnackbar('Failed to reject order', { variant: 'error' });
      }
        navigate(`/dashboard/rentals/pending-rentals`);
    }).catch(() => {
        enqueueSnackbar('Failed to reject order', { variant: 'error' });
    });
  };

   const punchout = (vendorId, vendorName) => {
      punchoutOrder(residentId, vendorId, userSiteId, orderId).then( res => {
        if(res.data.successUrl){
         const newWin = window.open(res.data.successUrl);
         if (!newWin || newWin.closed || typeof newWin.closed === 'undefined'){
           setConfirmPunchout({ url: res.data.successUrl, vendor: vendorName });
         }
        }
        else{
          enqueueSnackbar(`Punchout Error: ${res.data.errorMessage || "Punchout Failed"}`, { variant: 'error' });
        }
      }).catch(() => {
         enqueueSnackbar(`Something has gone wrong`, { variant: 'error' });
      });
    }

  return(
    <Stack direction = {'row'}>
      <Dialog
        maxWidth="md"
        open={confirmPunchout != null}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setConfirmPunchout(null);
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>Browser has blocked this puchout link</DialogTitle>
        <DialogContent sx={{ typography: 'body2' }}>
          <Box>Your browser has blocked this punchout link to {confirmPunchout?.vendor} from automatically opening.</Box>
          <Box>Please click open link to go to the punchout.</Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { window.open(confirmPunchout?.url); setConfirmPunchout(null); }}>
            Open link
          </Button>
          <Button variant="outlined" color="inherit" onClick={() => setConfirmPunchout(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
        {(orderStatus === 'pending' && hasSubmitAccess || orderStatus === "current" && isApproveAllowed && !isPunchoutOrder) &&
          <>
            <Button color="secondary" size="small" sx={{marginRight: '10px'}} variant="outlined" startIcon={<Iconify icon="eva:plus-outline" />} onClick={(event) => !isPunchoutOrder ? setMenuAnchorEl(event.currentTarget) : punchout(rows[0]?.vendorId, rows[0]?.vendorName)} >
              Add items
            </Button>
            <Menu anchorEl={menuAnchorEl} open={openMenu} onClose={() => setMenuAnchorEl(null)}>
              {categoryOptions?.map((category) => (
                <MenuItem
                  key={category.id}
                  onClick={() => {
                      setMenuAnchorEl(null);
                      navigate( `/dashboard/rentals/new-rental?facilityId=${facility}&residentId=${residentId}&category=${category.label}&orderId=${orderId}&orderStatus=${orderStatus}`);
                  }}
                >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {category.label}
                </Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        }
        <DownloadRentalOrderDropdown orderId={orderId} rows={rows} isPunchoutOrder={isPunchoutOrder}/>
        {orderStatus === 'pending' &&
          <ScopeGuard scopes={isPunchoutOrder ? ['Rental-Order-Punchout'] : ['Rental-Order-Submit']} allowAdmin>
          <Button color="secondary" onClick={() => setSplitOrder(true)} sx={{marginRight: "10px"}} variant="outlined" size="small" startIcon={<Iconify icon={'lucide:split'} />} >
              Split order
          </Button>
          </ScopeGuard>
        }
        <Stack item sx = {{ display: 'flex', alignItems: 'center' }}>
          {orderStatus === 'pending' ? (
            <Stack direction={'row'} spacing={1} sx={{marginRight: '10px'}}>
              <Button variant="outlined" color="error" size="small" disabled={!isApproveAllowed} onClick={rejectOrder}>
                Reject Order
              </Button>     
              <AwaitButton
                variant="contained"
                sx={{ mr: 1 }}
                size="small"
                color="primary"
                onClick={() => {
                  setOpenOrderApproveModal(true)
                }}
                disabled={!isApproveAllowed}
            >
                Approve Order
            </AwaitButton>  
            </Stack>
          ) : orderStatus === 'current' ? (
          <>
            <Stack direction={'row'} spacing={1} sx={{marginRight: '10px'}}>
              {isRequestMode ?  
              <AwaitButton
                variant="outlined"
                sx={{ mr: 1 }}
                size="small"
                color="primary"
                onClick={() => {
                    requestPickup()
                }}
                disabled={!isApproveAllowed}
                startIcon={<Iconify icon='mdi:check' />}
            >
                Confirm Request Pickup
            </AwaitButton> 
            : <Button
                color="primary" size="small" sx={{marginRight: '10px'}} variant="outlined"
                disabled={!isApproveAllowed || allRequested}
                onClick={enableRequestPickupMode}
                icon={<Iconify icon={'streamline:mail-send-email-message'} />}
              >
                {'Request Rental Pickup'}
              </Button>}
              {
                isConfirmMode ? 
                <AwaitButton
                variant="contained"
                sx={{ mr: 1 }}
                size="small"
                color="primary"
                onClick={() => {
                    confirmCloseOut()
                }}
                disabled={!isApproveAllowed}
                startIcon={<Iconify icon='mdi:check' />}
                >
                Confirm Close Out
            </AwaitButton> : 
            <Button
              color="primary" size="small" sx={{marginRight: '10px'}} variant="contained"
              disabled={!isApproveAllowed}
              onClick={ enableConfirmMode}
              startIcon={<Iconify icon={ 'gg:close-o'} />}
            >
              {isConfirmMode ? 'Confirm Close Out' : 'Close out Rental'}
            </Button>}
            </Stack>
          </>
          ) : (
              ''
          )}
        </Stack>
    </Stack>
      
  );
}
